import { useApiClient } from 'bff/ApiClientProvider';
import { secondsToMilliseconds } from 'date-fns';
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CompanyDetailsGetRequest,
  CompanyDetailsPostRequest,
  Result,
  ResultStates
} from 'bff/client';
import { useInvalidateProposalInformation } from 'bff/hooks/common/ProposalsInfosApi';
import { useInvalidateClientDetails } from 'bff/hooks/quotes/ClientDetailsApi';
import { UseDisplayErrorMutationOptions } from 'bff/types/display-error-props.type';

export const companyDetailsKeys = {
  base: () => ['company-details'] as const,
  fetch: (request: CompanyDetailsGetRequest) => [...companyDetailsKeys.base(), request],
  submit: () => [...companyDetailsKeys.base(), 'submit'] as const
};

export const useInvalidateCompanyDetails = () => {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: companyDetailsKeys.base(),
      refetchType: 'all'
    });
};

export const getCompanyDetailsOptions = (
  getCompanyDetailsViewRequest: CompanyDetailsGetRequest
) => {
  const { companyDetailsApi } = useApiClient();
  return queryOptions({
    staleTime: secondsToMilliseconds(30),
    queryKey: companyDetailsKeys.fetch(getCompanyDetailsViewRequest),
    queryFn: () => companyDetailsApi.companyDetailsGet(getCompanyDetailsViewRequest),
    select: (response) => response.data!
  });
};

export const useSaveCompanyDetails = (
  options?: UseDisplayErrorMutationOptions<Result, CompanyDetailsPostRequest>
) => {
  const { companyDetailsApi } = useApiClient();
  const invalidateClientDetails = useInvalidateClientDetails();
  const invalidateCompanyDetails = useInvalidateCompanyDetails();
  const invalidateProposalInformation = useInvalidateProposalInformation();

  return useMutation({
    mutationKey: companyDetailsKeys.submit(),
    mutationFn: (request: CompanyDetailsPostRequest) =>
      companyDetailsApi.companyDetailsPost(request),
    onSuccess: async (data, variables, ctx) => {
      if (data.resultState === ResultStates.DisplayError) {
        options?.onDisplayError?.(data);
      } else {
        await Promise.allSettled([
          invalidateClientDetails(),
          invalidateCompanyDetails(),
          invalidateProposalInformation()
        ]);
        options?.onSuccess?.(data, variables, ctx);
      }
    }
  });
};
